
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { DataGrid } from "@mui/x-data-grid";
import { Delete, Edit } from "@mui/icons-material";
import { Add as AddIcon } from "@mui/icons-material";
import { FileDownload, PictureAsPdf, Print } from "@mui/icons-material";
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { createOffer, deleteOffer, getAllProductOffer, updateOffer } from "../../../Vendor/State/Offer/Action";
import { getProducts } from "../../State/Product/Action";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";

const ProductOffer = () => {
  const { adminProduct, offer } = useSelector((store) => store);
  const dispatch = useDispatch();

  const todayDateTime = dayjs().format("YYYY-MM-DDTHH:mm");

  const [offerData, setOfferData] = useState({
    discountPercentage: "",
    startDate: "",
    endDate: "",
  });

  const [editingId, setEditingId] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(0);
  const [openAddOfferModal, setOpenAddOfferModal] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const fieldChanged = (event) => {
    setSelectedProductId(event.target.value);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setOfferData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getAllProductOffer)
  }, [dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !offerData.discountPercentage ||
      !offerData.startDate ||
      !offerData.endDate
    ) {
      toast.error("All fields are required");
      return;
    }

    if (offerData.discountPercentage <= 0) {
      toast.error("Discount percentage must be greater than 0");
      return;
    }

    if (offerData.endDate <= offerData.startDate) {
      toast.error("End date must be after the start date");
      return;
    }

    if (selectedProductId === 0) {
      toast.error("Please select a product");
      return;
    }

    const formattedOfferData = {
      ...offerData,
      startDate: dayjs(offerData.startDate).format("YYYY-MM-DDTHH:mm:ss"),
      endDate: dayjs(offerData.endDate).format("YYYY-MM-DDTHH:mm:ss"),
    };

    if (editingId) {
      if (offerData.endDate <= todayDateTime) {
        toast.error("End date must be in the future Date and Time");
        return;
      }
      dispatch(updateOffer(editingId, formattedOfferData));
    } else {
      if (offerData.startDate <= todayDateTime) {
        toast.error("Start date must be in the future Minute");
        return;
      }
      dispatch(createOffer(selectedProductId, formattedOfferData));
    }

    setOfferData({
      discountPercentage: "",
      startDate: "",
      endDate: "",
    });
    setSelectedProductId(0);
    setEditingId(null);
    setOpenAddOfferModal(false);
  };

  const handleOfferDelete = (offerId) => {
    setOfferToDelete(offerId);
    setOpenDialog(true);
  };

  const confirmDelete = () => {
    dispatch(deleteOffer(offerToDelete));
    setOpenDialog(false);
    setOfferToDelete(null);
  };

  const cancelDelete = () => {
    setOpenDialog(false);
    setOfferToDelete(null);
  };

  const handleOfferUpdate = (productId, item) => {
    setOfferData({
      discountPercentage: item.discountPercentage,
      startDate: dayjs(item.startDate).format("YYYY-MM-DDTHH:mm"),
      endDate: dayjs(item.endDate).format("YYYY-MM-DDTHH:mm"),
    });
    setSelectedProductId(productId);
    setEditingId(item.id);
    setOpenAddOfferModal(true);
  };

  const handleCloseAddOfferModal = () => {
    setOpenAddOfferModal(false);
    setOfferData({ discountPercentage: "", startDate: "", endDate: "" });
    setSelectedProductId(0);
    setEditingId(null);
  };

  const rows =
    adminProduct.products?.response
      ?.filter(
        (p) =>
          p.productStatus === "APPROVED" && p.vendor.approved && !p.vendor.isBlocked
      )?.flatMap((product) =>
        product.offers?.map((offer) => ({
          id: offer.id,
          productId: product.id,
          productTitle: "#" + product.id + "-" + product.title || "----",
          discountPercentage: offer.discountPercentage || 0,
          startDate: offer.startDate,
          endDate: offer.endDate,
        }))
      ) || [];

  const filteredRows = rows.filter((row) => {
    const query = searchQuery.toLowerCase();
    return (
      row.productTitle.toLowerCase().includes(query) ||
      String(row.discountPercentage).toLowerCase().includes(query) ||
      row.startDate.toLowerCase().includes(query) ||
      row.endDate.toLowerCase().includes(query)
    );
  });

  // Export, PDF, and Print Handlers
    const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => {
      return [
        index + 1,
        row.productId,
        row.productTitle,
        `${row.discountPercentage}%`,
        row.startDate ? dayjs(row.startDate).format("DD MMM YYYY HH:mm") : "----",
        row.endDate ? dayjs(row.endDate).format("DD MMM YYYY HH:mm") : "----",
      ].join(',');
    }).join('\n');

    const headers = [
      'S.No.',
      'Product ID',
      'Product Title',
      'Discount',
      'From Date',
      'To Date',
    ].join(',');

    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'offers.csv';
    link.click();
  };

  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Offer List", 14, 15);

    const tableColumn = [
      'S.No.',
      'Product ID',
      'Product Title',
      'Discount',
      'From Date',
      'To Date',
    ];
    const tableRows = [];

    exportRows.forEach((row, index) => {
      const offerData = [
        index + 1,
        row.productId,
        row.productTitle,
        `${row.discountPercentage}%`,
        row.startDate ? dayjs(row.startDate).format("DD MMM YYYY HH:mm") : "----",
        row.endDate ? dayjs(row.endDate).format("DD MMM YYYY HH:mm") : "----",
      ];
      tableRows.push(offerData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
    });

    doc.save('offers.pdf');
  };

  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = [
      'S.No.',
      'Product ID',
      'Product Title',
      'Discount',
      'From Date',
      'To Date',
    ];

    const tableContent = exportRows.map((row, index) => [index + 1,
    row.productId,
    row.productTitle,
    `${row.discountPercentage}%`,
    row.startDate ? dayjs(row.startDate).format("DD MMM YYYY HH:mm") : "----",
    row.endDate ? dayjs(row.endDate).format("DD MMM YYYY HH:mm") : "----",
    ]);

    const html = `
      <html>
        <head>
          <title>Offer List</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
            tr:nth-child(even) { background-color: #f5f5f5; }
          </style>
        </head>
        <body>
          <h2>Offer List</h2>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableContent.map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const writeAction = PermissionService.has({ category: "Admin", page: "Offers", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "productId", headerName: "Product ID", flex: 1 },
    { field: "productTitle", headerName: "Product Title", flex: 1 },
    { field: "discountPercentage", headerName: "Discount", width: 100, renderCell: (params) => `${params?.value}%` },
    { field: "startDate", headerName: "From Date & Time", flex: 1, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----", },
    { field: "endDate", headerName: "To Date & Time", flex: 1, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----", },
    ...(writeAction ? [
      {
        field: "actions",
        headerName: "Action",
        width: 100,
        renderCell: (params) => (
          <Box display="flex" gap={1}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleOfferUpdate(params.row.productId, params.row)}
              >
                <Edit fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleOfferDelete(params.row.id)}
              >
                <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ] : []),
  ];

  return (
    <div>

      <Dialog open={openAddOfferModal} onClose={handleCloseAddOfferModal} fullWidth maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", fontSize: "1.25rem", fontWeight: "bold", mt: 1, mb: 0.5, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
          {editingId ? "UPDATE OFFER" : "ADD NEW OFFER"}
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} mt={1}>
              <FormControl fullWidth size="small" variant="outlined"
              >
                <InputLabel>Product</InputLabel>
                <Select
                  id="productId"
                  name="productId"
                  label="Product"
                  value={selectedProductId}
                  onChange={fieldChanged}
                  disabled={editingId}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e5e7eb'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ef4444'
                    }
                  }}
                >
                  <MenuItem value={0} disabled>
                    Select Product...
                  </MenuItem>
                  {adminProduct.products?.response
                    ?.filter(
                      (p) =>
                        p.productStatus === "APPROVED" && p.vendor.approved && !p.vendor.isBlocked
                    )
                    ?.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        #{product.id}-{product.title}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Discount Percentage"
                name="discountPercentage"
                size="small"
                value={offerData.discountPercentage}
                onChange={handleChange}
                inputProps={{ maxLength: 2 }}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    transition: "all 0.2s ease-in-out",
                    "&:hover fieldset": {
                      borderColor: "#449AEF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#449AEF",
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Start Date & Time"
                name="startDate"
                type="datetime-local"
                size="small"
                value={offerData.startDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayDateTime }}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    transition: "all 0.2s ease-in-out",
                    "&:hover fieldset": {
                      borderColor: "#449AEF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#449AEF",
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="End Date & Time"
                name="endDate"
                type="datetime-local"
                size="small"
                value={offerData.endDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: offerData.startDate }}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    transition: "all 0.2s ease-in-out",
                    "&:hover fieldset": {
                      borderColor: "#449AEF",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#449AEF",
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center", gap: 2, p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Button
            variant="outlined"
            onClick={handleCloseAddOfferModal}
            sx={{
              color: "#449AEF",
              borderColor: "#449AEF",
              fontWeight: 600,
              fontSize: "0.85rem",
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "#449AEF",
                color: "#fff",
                borderColor: "#449AEF",
                boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
              },
              width: { xs: "100%", sm: "auto" },
              textAlign: "center"
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            onClick={handleSubmit}
            sx={{
              background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.85rem",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
              px: 3,
              py: 1.2,
              transition: "all 0.2s",
              "&:hover": {
                background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                transform: "scale(1.02)",
              },
              width: { xs: "100%", sm: "auto" },
              textAlign: "center"
            }}
          >
            {editingId ? "Update" : "Add"} Offer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={cancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this offer?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardHeader
          title={
            <Typography variant="h6" className="text-gray-700">
              OFFERS
            </Typography>
          }
          action={writeAction && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                sx={{
                  background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                    boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                    transform: "scale(1.02)",
                  },
                  cursor: "pointer",
                  p: 1,
                  borderRadius: '50%',
                }}
                onClick={() => {
                  setOpenAddOfferModal(true);
                  setEditingId(null);
                }}
              >
                <AddIcon />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#449AEF", fontSize: "1.2rem", letterSpacing: 0.2 }}
              >
                Add Offer
              </Typography>
            </Box>
          )}
        />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExportCSV}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Print
              </Button>
            </Box>
            <Box>
              <TextField
                label="Search Offers"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: { xs: '100%', sm: '300px' } }}
              />
            </Box>
          </Box>
          <Box sx={{ height: "100%", width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 75, 100]}
                checkboxSelection
              onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
              rowSelectionModel={selectedRowIds}
              disableSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={offer.loading || adminProduct.loading}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  padding: "8px",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
              getRowId={(row) => row.id}
            />
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductOffer;
